import { useCallback, useEffect, useState } from 'react';
import { Briefcase, Search, Send, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import PageShell from '../components/PageShell';
import DataTable, { type Column } from '../components/DataTable';
import Modal from '../components/Modal';
import StatusPill from '../components/StatusPill';
import FilterBar, {
  controlClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../components/FilterBar';
import OpenInAppButton from '../components/OpenInAppButton';
import AppViewTabs from '../components/AppViewTabs';
import { formatDate } from '../lib/format';
import { findContracts, fetchIntegrationStatus, sendContract, type RawIntegrationStatus } from '../lib/api';

type Row = Record<string, unknown>;

function str(row: Row, ...keys: string[]): string | null {
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined && value !== null && value !== '' && typeof value !== 'object') {
      return String(value);
    }
  }
  return null;
}

function Contracts() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [applied, setApplied] = useState({ query: '', status: 'all' });
  const [sendFor, setSendFor] = useState<string | null>(null);
  const [integration, setIntegration] = useState<RawIntegrationStatus | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [statusResult, result] = await Promise.all([
      fetchIntegrationStatus('documenso'),
      findContracts(applied.query || undefined, applied.status, 50),
    ]);
    setIntegration(statusResult.ok ? statusResult.data ?? null : null);
    if (result.ok && result.data) setRows(result.data.data ?? []);
    else setRows([]);
    setLoading(false);
  }, [applied]);

  useEffect(() => {
    load();
  }, [load]);

  const columns: Column<Row>[] = [
    { key: 'title', header: 'Title', render: (r) => str(r, 'title', 'name', 'documentName') ?? '—' },
    { key: 'id', header: 'ID', render: (r) => str(r, 'id', 'documentId') ?? '—' },
    { key: 'created', header: 'Created', render: (r) => formatDate(str(r, 'createdAt', 'created_at', 'date')) },
    { key: 'status', header: 'Status', render: (r) => <StatusPill status={str(r, 'status') ?? 'draft'} /> },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (r) => {
        const id = str(r, 'id', 'documentId');
        if (!id) return null;
        return (
          <button type="button" onClick={() => setSendFor(id)} className={secondaryButtonClass}>
            <Send size={14} /> Send
          </button>
        );
      },
    },
  ];

  const applyFilters = () => setApplied({ query, status });

  const documensoConnected = integration?.connected === true;
  const emptyMessage = documensoConnected
    ? 'No contracts in Documenso yet. Upload a document at sign.zerotail.org to get started.'
    : integration?.configured
      ? 'Documenso is configured but not connected. Check the API token in Admin → Integrations.'
      : 'Connect Documenso or adjust your search to find contracts.';

  const dashboard = (
    <>
      <FilterBar
        actions={
          <>
            <OpenInAppButton integrationKey="documenso" />
            <button type="button" onClick={load} className={secondaryButtonClass}>
              <RefreshCw size={16} /> Refresh
            </button>
            <button type="button" onClick={applyFilters} className={secondaryButtonClass}>
              <Search size={16} /> Search
            </button>
          </>
        }
      >
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
          placeholder="Search contracts…"
          className={`w-56 ${controlClass}`}
        />
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={controlClass}>
          <option value="all">All statuses</option>
          <option value="draft">Draft</option>
          <option value="sent">Sent</option>
          <option value="completed">Completed</option>
        </select>
      </FilterBar>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r, i) => str(r, 'id', 'documentId') ?? String(i)}
        loading={loading}
        emptyTitle="No contracts found"
        emptyMessage={emptyMessage}
        emptyIcon={<Briefcase size={32} />}
      />

      <SendContractModal contractId={sendFor} onClose={() => setSendFor(null)} onDone={load} />
    </>
  );

  return (
    <PageShell
      title="Contracts"
      subtitle="Find and send contracts for signature via Documenso."
    >
      <AppViewTabs appId="documenso" embedTitle="Documenso" dashboard={dashboard} />
    </PageShell>
  );
}

function SendContractModal({
  contractId,
  onClose,
  onDone,
}: {
  contractId: string | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!contractId) return;
    setSubmitting(true);
    const result = await sendContract(contractId, {
      recipient_email: email.trim() || undefined,
      message: message.trim() || undefined,
    });
    setSubmitting(false);
    if (result.ok && result.data?.sent) {
      toast.success('Contract sent for signature');
      setEmail('');
      setMessage('');
      onClose();
      onDone();
    } else if (result.ok) {
      toast.error('Contract could not be sent');
    }
  };

  return (
    <Modal
      open={contractId !== null}
      onClose={onClose}
      title="Send for signature"
      description={contractId ? `Document ${contractId}` : undefined}
      footer={
        <>
          <button type="button" onClick={onClose} className={secondaryButtonClass}>
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} disabled={submitting} className={primaryButtonClass}>
            {submitting ? 'Sending…' : 'Send'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-200">Recipient email (optional)</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="signer@example.com"
            className={`w-full ${controlClass}`}
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-200">Message (optional)</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
            placeholder="Please review and sign."
            className={`w-full ${controlClass}`}
          />
        </div>
      </div>
    </Modal>
  );
}

export default Contracts;
